/**
 * TunnelOrgChip — the SANDBOX top-bar org dropdown (in-place switching).
 *
 * The tunnel model makes the organization the outermost frame around all five
 * app modes; every mode needs a top-bar org affordance so the identity never
 * goes invisible inside a module page:
 *   demo                  → DemoCompanySwitcher (companies, grouped by profile)
 *   development / quality → DemoCompanySwitcher too (same companies, relabeled
 *                           "Organization" — identity continuity, P2-lite)
 *   production            → ActiveOrgSwitcher (real orgs, amber)
 *   sandbox               → THIS chip: the curated sandbox org list (shared
 *                           with the homepage picker via useSandboxOrgOptions),
 *                           picked IN PLACE — the page stays mounted and
 *                           re-scopes, no round-trip to the home workspace.
 *
 * When no sandbox org is picked, the label falls back to the ridden company
 * identity (tunnel continuity) and the dropdown invites a real org choice.
 */
import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, ChevronDown, Check } from 'lucide-react'
import { useAppStore } from '@/store/appStore'
import { getDemoCompany } from '@/types/demoCompanies'
import { isInternalSurface } from '@/config/productSurface'
import { useSandboxOrgOptions } from '@/hooks/useSandboxOrgOptions'
import { cn } from '@/lib/utils'

export function TunnelOrgChip() {
  const appMode = useAppStore((s) => s.appMode)
  const currentCompanyId = useAppStore((s) => s.currentCompanyId)
  const activeOrganizationId = useAppStore((s) => s.activeOrganizationId)
  const setActiveOrganization = useAppStore((s) => s.setActiveOrganization)
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  // Self-disabled outside sandbox — safe to call before the mode gate below.
  const { options, loading } = useSandboxOrgOptions()

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  if (appMode !== 'sandbox' || !isInternalSurface()) return null

  const demoCompanyName = currentCompanyId
    ? getDemoCompany(currentCompanyId)?.name ?? currentCompanyId
    : null
  const activeOrgName = activeOrganizationId
    ? (options.find((o) => o.id === activeOrganizationId)?.name ?? activeOrganizationId)
    : null
  // Same continuity precedence as the homepage tunnel bar: the picked sandbox
  // org wins; otherwise the ridden company identity carries through as a label.
  const label = activeOrgName ?? demoCompanyName ?? 'No org selected'

  function pick(orgId: string | null) {
    setOpen(false)
    if (orgId === activeOrganizationId) return
    // In place: the current page stays mounted; org-scoped queries re-key off
    // activeOrganizationId and re-render with the new org's data.
    setActiveOrganization(orgId)
  }

  return (
    <div ref={ref} className="relative min-w-0">
      <button
        onClick={() => setOpen((v) => !v)}
        title={`Sandbox organization: ${label} — click to switch`}
        aria-label="Switch sandbox organization"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg border border-transparent text-text-secondary text-body-sm font-medium hover:border-border hover:bg-surface-muted active:scale-95 transition-colors"
      >
        <Building2 size={14} className="flex-shrink-0 text-brand-500" />
        <span className="max-w-[110px] sm:max-w-[160px] truncate">{label}</span>
        <ChevronDown size={13} className={`flex-shrink-0 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="fixed inset-x-2 top-14 sm:absolute sm:inset-x-auto sm:left-0 sm:top-11 sm:w-72 max-h-[85vh] overflow-y-auto bg-white rounded-lg shadow-dropdown border border-border py-1 animate-slide-in-up z-50">
          <div className="px-3 py-2 border-b border-border">
            <div className="text-body-sm font-semibold text-text-primary">Switch sandbox organization</div>
            <div className="text-[11px] text-text-tertiary">Real orgs only — the page re-scopes in place</div>
          </div>

          {loading ? (
            <div className="px-3 py-2 text-body-sm text-text-tertiary">Loading organizations…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-body-sm text-text-tertiary">No sandbox organizations with data yet</div>
          ) : (
            options.map((o) => {
              const isActive = o.id === activeOrganizationId
              return (
                <button
                  key={o.id}
                  onClick={() => pick(o.id)}
                  className={cn(
                    'w-full text-left px-3 py-2 flex items-center gap-2 transition-colors',
                    isActive ? 'bg-brand-50' : 'hover:bg-surface-muted/50'
                  )}
                >
                  <Building2 size={14} className={cn('flex-shrink-0', isActive ? 'text-brand-600' : 'text-text-tertiary')} />
                  <span className={cn('flex-1 truncate text-body-sm', isActive ? 'font-medium text-brand-700' : 'text-text-primary')}>{o.name}</span>
                  {o.id === 'org_mach12' && (
                    <span className="text-[8px] uppercase tracking-wide font-bold text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 flex-shrink-0">Platform</span>
                  )}
                  {isActive && <Check size={14} className="flex-shrink-0 text-brand-600" />}
                </button>
              )
            })
          )}

          <div className="border-t border-border mt-1 pt-1">
            <button
              onClick={() => { setOpen(false); navigate('/') }}
              className="w-full text-left px-3 py-2 text-body-sm text-text-secondary hover:bg-surface-muted/50 transition-colors"
            >
              Open the home workspace →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
